import type { Content, GroupField } from '@prismicio/client';
import {
  Dispatch,
  memo,
  Ref,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import type { Simplify } from '../../../../../prismicio-types';

import 'swiper/css';
import './swiper.scss';

import { screenSizes } from '@/constants';
import { useScreenSize } from '@/hooks';
import { TestimonialCard } from './TestimonialCard/TestimonialCard';

const breakpoints = {
  [screenSizes.mobile]: {
    slidesPerView: 1.1,
  },
  [screenSizes.tablet]: {
    slidesPerView: 1.37,
  },
  [screenSizes.desktop]: {
    slidesPerView: 1.37,
  },
};

export type NavigationRef = {
  nextSlide: () => void;
  prevSlide: () => void;
};

interface TestimonialsListProps {
  testimonials: GroupField<
    Simplify<Content.TestimonialsSliceDefaultPrimaryTestimonialsItem>
  >;
  isVisible: boolean;
  setSlideNumber: Dispatch<
    SetStateAction<{
      first: boolean;
      last: boolean;
    }>
  >;
  navigationRef: Ref<NavigationRef>;
}

const TestimonialsList = ({
  testimonials,
  isVisible,
  setSlideNumber,
  navigationRef,
}: TestimonialsListProps) => {
  const [centeredSlide, setCenteredSlide] = useState<boolean>(false);
  const [spaceBetween, setSpaceBetween] = useState<8 | 16>(16);
  const swiperRef = useRef<SwiperClass | null>(null);
  const { width } = useScreenSize();

  useEffect(() => {
    const isMaxWidth = width < 1150;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCenteredSlide(isMaxWidth);
    setSpaceBetween(isMaxWidth ? 8 : 16);
  }, [width]);

  const nextSlide = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const prevSlide = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  useImperativeHandle(navigationRef, () => ({
    nextSlide,
    prevSlide,
  }));

  const checkIfBoundarySlide = (swiperInstance: SwiperClass) => {
    const isLast =
      swiperInstance.activeIndex === swiperInstance.slides.length - 1;
    const isFirst = swiperInstance.activeIndex === 0;

    if (isFirst) {
      setSlideNumber((prev) => ({ ...prev, first: true }));
    }

    if (isLast) {
      setSlideNumber((prev) => ({ ...prev, last: true }));
    }
    if (!isFirst && !isLast) setSlideNumber({ first: false, last: false });
  };

  return (
    <>
      <Swiper
        spaceBetween={spaceBetween}
        loop={false}
        className="mySwiper"
        centeredSlides={centeredSlide}
        onSwiper={(swiperInstance) => {
          swiperRef.current = swiperInstance;
        }}
        onSlideChange={(swiperInstance) => {
          checkIfBoundarySlide(swiperInstance);
        }}
        breakpoints={breakpoints}
        slidesOffsetAfter={spaceBetween}
      >
        {testimonials.map((item, index) => (
          <SwiperSlide key={`${index} ${item.text}`}>
            <TestimonialCard testimonial={item} isVisible={isVisible} />
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  );
};

export default memo(TestimonialsList);
